import React, { FunctionComponent, useState, useEffect } from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import ColumnGroupTable from '@murphy-frontend/web-core/components/ColumnGroupTable';
import { createColumnGroup, sortByKey } from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils";
import { ColumnGroup } from '@murphy-frontend/web-core/components/ColumnGroupTable/Models';
import { RiArrowDropDownLine, RiArrowDropUpLine } from 'react-icons/ri';

interface Rows {
  responseSelectedAlternative: string,
  responseAnswer: string,
  id: string,
  date: string,
}
interface MBSResponseTableProps {
  rows: Rows[],
  translations: Record<string, string>,
};

const MBSResponseTable: FunctionComponent<MBSResponseTableProps> = ({
  rows,
  translations,
}) => {
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState(false);
  const [currentSortingColumn, setCurrentSortingColumn] = useState<string>('name');
  const [pageNum, setPageNum] = useState<number>(1);
  const [sortedAscending, setSortedAscending] = useState<boolean>(true);
  const [columnGroups, setColumnGroups] = useState<ColumnGroup[]>([]);

  const onClickRow = (id) => {
    navigate(`/pbsresponse/${id}`);
  };

  const navigateHandler = (num: number) => {
    setPageNum(num)
  }

  const sortByColumn = (columnKey: string) => {
    setCurrentSortingColumn(columnKey);
    const newSortingOrder = !sortedAscending;
    setSortedAscending(newSortingOrder);
  };

  const respondantColGroup = (data) => {
    const respondantColumn = [
      {
        columnKey: "userName",
        displayName: translations.respondant || "Respondant",
        isSortable: true,
        size: 'column-medium-large-alerts',
        onClickColumn: sortByColumn,
      },
    ];
    const respondantRows = data.map((row) => ([{
      columnKey: 'userName',
      id: `userName-${row.id}`,
      rowId: row.id,
      title: row.userName,
      color: row.dateAnswered ? '#FFFFFF' : '#FCF4EE',
      size: row.id === expandedId ? 'column-medium-large-alerts1' : 'column-medium-large-alerts',
      displayValue: (
        <div className="vertical-aligner">
          <span>
            {row.userName}
          </span>
        </div>
      )
    }]));
    return createColumnGroup("", respondantColumn, respondantRows);
  };

  const dateColumnGroup = (data) => {
    const dateColumn = [
      {
        columnKey: "dateAnswered",
        displayName: translations.daterespond || "Date Responded",
        isSortable: true,
        size: 'column-medium-large-alerts',
        onClickColumn: sortByColumn,
      },
    ];
    const dateRows = data.map((row) => ([{
      columnKey: 'dateAnswered',
      id: `dateAnswered-${row.id}`,
      rowId: row.uniqueKey,
      color: row.dateAnswered ? '#FFFFFF' : '#FCF4EE',
      size: row.id === expandedId ? 'column-medium-large-alerts1' : 'column-medium-large-alerts',
      title: row.dateAnswered,
      displayValue: (
        <div className="vertical-aligner">
          <span>
            {row.dateAnswered}
          </span>
        </div>
      )
    }]));
    return createColumnGroup("", dateColumn, dateRows);
  };


  const alternativeColGroup = (data) => {
    const alternativeCol = [
      {
        columnKey: "responseSelectedAlternative",
        displayName: translations.ynresponse || "Y/N Response",
        isSortable: true,
        size: 'column-medium-large-alerts',
        onClickColumn: sortByColumn,
      },
    ];
    const alternativeRows = data.map((row) => ([{
      columnKey: 'responseSelectedAlternative',
      id: `responseSelectedAlternative-${row.id}`,
      rowId: row.id,
      color: row.dateAnswered ? '#FFFFFF' : '#FCF4EE',
      size: row.id === expandedId ? 'column-medium-large-alerts1' : 'column-medium-large-alerts',
      title: row.responseSelectedAlternative,
      displayValue: (
        <div className="vertical-aligner">
          <span>
            {row.responseSelectedAlternative}
          </span>
        </div>
      )
    }]));
    return createColumnGroup("", alternativeCol, alternativeRows);
  };

  const freetextColGroup = (data) => {
    const freetextCol = [
      {
        columnKey: "responseAnswer",
        displayName: translations.freetext || "Free-text answer",
        isSortable: true,
        size: 'column-medium-large-alerts',
        onClickColumn: sortByColumn,
      },
    ];
    const freetextRows = data.map((row) => ([{
      columnKey: 'responseAnswer',
      id: `responseAnswer-${row.id}`,
      rowId: row.id,
      color: row.dateAnswered ? '#FFFFFF' : '#FCF4EE',
      size: row.id === expandedId ? 'column-medium-large-alerts1' : 'column-medium-large-alerts',
      title: row.responseAnswer,
      displayValue: (
        <div className="vertical-aligner">
          <span style={row.id === expandedId ? { width: '12rem' } : {
            width: '12rem', overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
          }}>
            {row.responseAnswer}
          </span>
          {row.responseAnswer && (row.id !== expandedId ? <RiArrowDropDownLine title="Info" onClick={() => setExpandedId(row.id)} size={30} /> : <RiArrowDropUpLine title="Info" onClick={() => setExpandedId(-1)} size={30} />)}
        </div>
      )
    }]));
    return createColumnGroup("", freetextCol, freetextRows);
  };

  useEffect(() => {
    if (rows) {
      const allColumnGroups = [];
      const data = [...rows];
      if (currentSortingColumn) {
        data.sort(sortByKey(currentSortingColumn, sortedAscending));
      }

      const dateColGroup = dateColumnGroup(data);
      const respColGroup = respondantColGroup(data);
      const alterColGroup = alternativeColGroup(data);
      const textColGroup = freetextColGroup(data);
      allColumnGroups.push(dateColGroup);
      allColumnGroups.push(respColGroup);
      allColumnGroups.push(alterColGroup);
      allColumnGroups.push(textColGroup);
      setColumnGroups(allColumnGroups);
    }
  }, [sortedAscending, currentSortingColumn, translations, pageNum, expandedId, rows]);


  return (
    <ColumnGroupTable
      columngroups={columnGroups}
      sortedByColumn={currentSortingColumn}
      isSortedAscending={sortedAscending}
      hidePageInputs={true}
      navigateHandler={navigateHandler}
    />
  );
}

export default MBSResponseTable;
